import { isDarkTheme, resolveTheme } from "./themePresets";
export { THEMES, isDarkTheme } from "./themePresets";

const STORAGE_KEY = "app-theme";
let sessionTheme;

export const getChartTheme = (theme) => ({
  text: theme === "light" ? "rgba(107, 114, 128, 0.8)" : theme === "dark" ? "#b3c0d0" : resolveTheme(theme).colors.muted,
  legend: theme === "light" ? undefined : theme === "dark" ? "#c6d2df" : resolveTheme(theme).colors.text,
  grid:
    isDarkTheme(theme)
      ? "rgba(179, 192, 208, 0.18)"
      : "rgba(107, 114, 128, 0.1)",
});

// Keep chart categories recognizable while reducing saturated fills on dark cards.
export const getChartColor = (color, theme) => {
  if (!isDarkTheme(theme)) return color;
  const match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/.exec(color);
  if (!match) return color;
  const neutral = [116, 138, 158];
  const channels = neutral.map((value, index) =>
    Math.round(Number(match[index + 1]) * 0.68 + value * 0.32),
  );
  return match[4]
    ? `rgba(${channels.join(", ")}, ${Math.min(Number(match[4]), 0.78)})`
    : `rgb(${channels.join(", ")})`;
};

export const getTheme = () => {
  if (sessionTheme) return sessionTheme;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return resolveTheme(saved).id;
  } catch (_) {
    // Storage can be unavailable in private browsing.
  }
  return "light";
};

export const applyTheme = (theme) => {
  const preset = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle("dark", preset.mode === "dark");
  root.dataset.theme = preset.id;
  root.toggleAttribute("data-custom-theme", !["light", "dark"].includes(preset.id));
  root.style.colorScheme = preset.mode;
  // Preserve the original Classic/Dark CSS; named themes override the shared
  // tokens on <html>, so portals and native popups inherit the same palette.
  const tokens = {
    page: preset.colors.page, surface: preset.colors.surface, raised: preset.colors.raised,
    hover: preset.colors.hover, stripe: preset.colors.stripe, border: preset.colors.border,
    "control-border": preset.colors.control, text: preset.colors.text, muted: preset.colors.muted,
    primary: preset.colors.primary, header: preset.colors.header, navy: preset.colors.navy,
    focus: preset.colors.focus, "on-primary": preset.colors["on-primary"] || "#ffffff",
  };
  Object.entries(tokens).forEach(([name, value]) => {
    if (["light", "dark"].includes(preset.id)) root.style.removeProperty(`--pc-${name}`);
    else root.style.setProperty(`--pc-${name}`, value);
  });
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", preset.id === "dark" ? "#101820" : preset.id === "light" ? "#0d4b82" : preset.colors.header);
};

export const setTheme = (theme) => {
  const selected = resolveTheme(theme).id;
  try {
    localStorage.setItem(STORAGE_KEY, selected);
    sessionTheme = undefined;
  } catch (_) {
    // The preference still works for the current page.
    sessionTheme = selected;
  }
  applyTheme(selected);
  window.dispatchEvent(new CustomEvent("app-theme-change", { detail: selected }));
};

export const watchTheme = () => {
  const update = () => {
    const theme = getTheme();
    applyTheme(theme);
    window.dispatchEvent(new CustomEvent("app-theme-change", { detail: theme }));
  };
  const handleStorage = (event) => {
    if (event.key === STORAGE_KEY || event.key === null) update();
  };
  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
};
